import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { toast } from 'sonner';
import { RotateCcw, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/hooks/use-permission';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { RECORD_LABELS, useRecycleBin, useRestore, type RecordType } from './api';

/** Deleted leads, contacts, companies and deals, for 90 days (AmoCRM, HubSpot). */
export function RecycleBinPage() {
  const { can } = usePermission();
  const types = (['lead', 'contact', 'company', 'deal'] as RecordType[]).filter((type) => can(`crm.${type}.delete`));
  const [type, setType] = useState<RecordType>(types[0] ?? 'lead');
  const { data, isLoading } = useRecycleBin(type, types.includes(type));
  const restore = useRestore();

  return (
    <div>
      <PageHeader
        title="Recycle bin"
        description={`Deleted records stay here for ${data?.retentionDays ?? 90} days. Restore puts one back exactly as it was, with its history.`}
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Recycle bin' }]}
      />
      {types.length === 0 ? (
        <p className="text-sm text-muted-foreground">Your role cannot delete or restore records.</p>
      ) : (
        <>
          <div className="mb-4 flex gap-1 border-b">
            {types.map((value) => (
              <button
                key={value}
                onClick={() => setType(value)}
                className={cn(
                  '-mb-px border-b-2 px-4 py-2 text-sm',
                  type === value ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground',
                )}
              >
                {RECORD_LABELS[value]}
              </button>
            ))}
          </div>
          <Card>
            <CardContent className="overflow-x-auto pt-4">
              {isLoading ? (
                <PageLoader />
              ) : !data?.rows.length ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  <Trash2 className="mx-auto mb-2 h-8 w-8 opacity-40" />
                  Nothing deleted recently.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Record</TableHead>
                      <TableHead>Deleted</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <p className="font-medium">{row.name}</p>
                          {row.detail && <p className="text-xs text-muted-foreground">{row.detail}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDateTime(row.deletedAt)}</TableCell>
                        <TableCell className="text-end">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={restore.isPending}
                            onClick={() => restore.mutate({ type, id: row.id }, { onSuccess: () => toast.success(`${row.name} restored`) })}
                          >
                            <RotateCcw /> Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
